import amplitudeKit from '@mparticle/web-amplitude-kit';
import mParticle from '@mparticle/web-sdk';

import debounce from 'lodash/debounce';
import forOwn from 'lodash/forOwn';
import isArray from 'lodash/isArray';
import once from 'lodash/once';
import startCase from 'lodash/startCase';
import noop from 'lodash/noop';

import { pathOr, pick } from 'ramda';

import { useEffect } from 'react';
import { useHistory } from 'react-router-dom';

let areCookiesAccepted;

/** @enum {string} */
const EVENT_TYPE = {
  UNKNOWN: 'Unknown',
  NAVIGATION: 'Navigation',
  LOCATION: 'Location',
  SEARCH: 'Search',
  TRANSACTION: 'Transaction',
  USER_CONTENT: 'UserContent',
  USER_PREFERENCE: 'UserPreference',
  SOCIAL: 'Social',
  OTHER: 'Other',
};

const EventType = {
  [EVENT_TYPE.UNKNOWN]: 0,
  [EVENT_TYPE.NAVIGATION]: 1,
  [EVENT_TYPE.LOCATION]: 2,
  [EVENT_TYPE.SEARCH]: 3,
  [EVENT_TYPE.TRANSACTION]: 4,
  [EVENT_TYPE.USER_CONTENT]: 5,
  [EVENT_TYPE.USER_PREFERENCE]: 6,
  [EVENT_TYPE.SOCIAL]: 7,
  [EVENT_TYPE.OTHER]: 8,
};

/** @enum {string} */
const EVENT_CATEGORY = {
  UNKNOWN: 'Unknown',
  FLOW_STEP: 'Flow Step',
  LEAVE_FLOW: 'Leave Flow',
  LANGUAGE_CHANGE: 'Language Change',
  CHANGE_REGION: 'Change Region',
  FRESH_CHAT: 'Fresh Chat',
  RECAPTCHA: 'ReCaptcha',
};

/** @enum {string} */
const EVENT_ACTION = {
  UNKNOWN: 'Unknown',
  CTA_CLICK: 'CTA Click',
  FORM_SUBMIT: 'Form Submit',
  SELECT_OPTION: 'Select Option',
  LEAVE_BROWSER_TAB: 'Leave Browser Tab',
  BACK_BUTTON_CLICK: 'Back Button Click',
  APPLICATION_STATUS_CHANGE: 'Application Status Change',
};

const ANALYTICS_EVENTS = {
  leaveTab: {
    name: 'Browser Tab Left',
    description: 'Merchant leaves the current browser tab',
    action: EVENT_ACTION.LEAVE_BROWSER_TAB,
    category: EVENT_CATEGORY.LEAVE_FLOW,
    type: EVENT_TYPE.NAVIGATION,
    properties: ['currentPage'],
  },
  backButton: {
    name: 'Back Button Clicked',
    description: 'Merchant goes back to previous page',
    action: EVENT_ACTION.BACK_BUTTON_CLICK,
    category: EVENT_CATEGORY.FLOW_STEP,
    type: EVENT_TYPE.NAVIGATION,
    properties: ['currentPage', 'destinationPage'],
  },
};

class Event {
  /**
   * @param {object} opts
   * @param {string} opts.name
   * @param {string} opts.description
   * @param {EVENT_ACTION} opts.action
   * @param {EVENT_CATEGORY} opts.category
   * @param {EVENT_TYPE[]} opts.type
   * @param {string[]} opts.properties each property value has to be a string
   * @param {string[]} opts.userAttributes each attribute can either be string,
   *  an array of strings, or null. this are not part of the event. these attributes
   *  will directly register to the user the event was fired by
   */
  constructor({
    name = '',
    description = '',
    action = EVENT_ACTION.UNKNOWN,
    category = EVENT_CATEGORY.UNKNOW,
    type = EVENT_TYPE.UNKNOWN,
    properties = [],
    userAttributes = [],
  }) {
    this.name = name;
    this.description = description;
    this.action = action;
    this.category = category;
    this.type = type;
    this.properties = properties;
    this.userAttributes = userAttributes;
  }
}

// NOTE The window.mParticle.EventType object may be null because of async loading
// which is odd given the logEvent is set immediately
const getEventType = type => {
  return pathOr(EventType[type], ['mParticle', 'EventType', type], window);
};

/**
 * @param {Event} event
 * @param {object} customData
 */
const logEvent = (
  {
    name = '',
    description = '',
    action = '',
    category = '',
    type = EVENT_TYPE.UNKNOWN,
    properties = [],
    userAttributes = [],
  } = {},
  customData = {},
) => {
  if (!areCookiesAccepted) {
    return;
  }

  const mParticleType = getEventType(type);
  const filteredProperties = pick(properties, customData);

  const mParticleEvent = {
    description,
    action,
    category,
    ...filteredProperties,
  };

  mParticle.ready(function() {
    mParticle.logEvent(startCase(name), mParticleType, mParticleEvent);
    if (userAttributes.length) {
      setUserAttributes({ userAttributes }, { ...customData });
    }
  });
};

const logPageView = debounce(() => {
  if (!areCookiesAccepted) {
    return;
  }

  mParticle.ready(function() {
    mParticle.logPageView('Onboarding Flow Step', {
      page: window.location.pathname.toString(),
    });
  });
}, 1000);

const setUserAttributes = ({ userAttributes = [] }, customData = {}) => {
  const currentUser = mParticle.Identity.getCurrentUser();

  const filteredAttributes = pick(userAttributes, customData);

  forOwn(filteredAttributes, (value, key) => {
    if (currentUser) {
      return isArray(value)
        ? currentUser.setUserAttributeList(key, value)
        : currentUser.setUserAttribute(key, value);
    }
  });
  return null;
};

const setUserTag = tag => {
  if (mParticle && mParticle.Identity) {
    const currentUser = mParticle.Identity.getCurrentUser();
    if (currentUser) {
      currentUser.setUserTag(tag);
    }
  }
};

const login = ({ customerid, callback = () => {} }) => {
  const identityRequest = {
    userIdentities: {
      customerid,
    },
  };
  if (customerid != null) {
    mParticle.Identity.login(identityRequest, callback);
  }
};

const logout = (callback = () => {}) => mParticle.Identity.logout({}, callback);

const addEmailToUser = email => {
  mParticle.Identity.modify({ userIdentities: { email } });
};

const initializeAnalytics = once((analyticsInstance, { isEnvironmentE2ETest }) => {
  const functionsToFilter = [
    'login',
    'logout',
    'addEmailToUser',
    'setUserAttributes',
    'logPageView',
    'logEvent',
  ];
  if (isEnvironmentE2ETest) {
    functionsToFilter.map(fnName => (analyticsInstance[fnName] = noop));
  }
});

const initializeMParticle = once((apiKey, mParticleConfig = {}) => {
  amplitudeKit.register(mParticleConfig);
  mParticle.init(apiKey, mParticleConfig);
});

const watchForUserLeavingCurrentTab = () => {
  window.onbeforeunload = function() {
    logEvent(ANALYTICS_EVENTS.leaveTab, { currentPage: window.location.pathname.toString() });
  };
};

/**
 * @param {*} apiKey
 * @param {*} mParticleConfig
 */
const useMParticleTracking = (apiKey, mParticleConfig) => {
  areCookiesAccepted = mParticleConfig.areCookiesAccepted;

  // note on mParticleConfig.isEnvironmentE2ETest: you can't declare useEffect conditionally
  // as this causes React to complain about less effects than expected, so instead you need to
  // simply pass a noop function for the effect to do nothing based on your condition.
  useEffect(
    mParticleConfig.isEnvironmentE2ETest
      ? noop
      : () => initializeMParticle(apiKey, mParticleConfig),
    [],
  );

  // we pass an empty array as dependency below as will make the effect not depended on
  // any thing else, so it will only trigger when the component mounts.
  useEffect(mParticleConfig.isEnvironmentE2ETest ? noop : watchForUserLeavingCurrentTab, []);

  const history = useHistory();

  useEffect(
    mParticleConfig.isEnvironmentE2ETest
      ? noop
      : () => {
          return history.listen(() => {
            logPageView();
          });
        },
    [history],
  );
};

const useAnalyticsWith = (
  analyticsInstance,
  { areCookiesAccepted: _areCookiesAccepted, isEnvironmentE2ETest },
) => {
  areCookiesAccepted = _areCookiesAccepted;
  useEffect(() => initializeAnalytics(analyticsInstance, { isEnvironmentE2ETest }), []);
};

export const analytics = {
  Event,
  useAnalyticsWith,
  useMParticleTracking,
  login,
  logout,
  addEmailToUser,
  initializeMParticle,
  setUserAttributes,
  setUserTag,
  logPageView,
  logEvent,
  ANALYTICS_EVENTS,
  EVENT_TYPE,
  EVENT_CATEGORY,
  EVENT_ACTION,
};
